var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import GlobalEvents from "../GlobalEvents.js";
import DH from "../helper/DateHelper.js";
import DomSync from "../helper/DomSync.js";
import VersionHelper from "../helper/VersionHelper.js";
import PickerField from "./PickerField.js";
import TimePicker from "./TimePicker.js";
const numbers = /\d+/, delimiterRe = /\d+(\D)\d+/, names = ["hour", "minute", "second"], magnitudes = [3600, 60, 1], getMethod = [
  "getHours",
  "getMinutes",
  "getSeconds"
], styles = `
        :host {
            display:flex
        }
        * {
            font-size:inherit;
            font-family:inherit;
            color:inherit;
            background-color:inherit
        }
        input,button {
            padding:0;
            margin:0;
            border:0 none;
            outline:0 none;
            background-color:var(--timefield-input-background-color);
            text-align:center
        }
        input {
            width:2ch;
        }
        #am-pm {
            margin-inline:5px
        }
        #am-pm:focus-visible {
            outline:none;
            background-color:var(--timefield-button-background-color)
        }`;
class TimeInput extends HTMLElement {
  constructor() {
    super();
    const me = this, root = me._root = me.attachShadow({
      mode: "open",
      delegatesFocus: true
    });
    me.internals_ = me.attachInternals();
    me.byRef = /* @__PURE__ */ Object.create(null);
    root.addEventListener("keydown", me.onKeyDown.bind(me));
    root.addEventListener("focusin", me.onFocusIn.bind(me));
    root.addEventListener("click", me.onClick.bind(me));
    root.addEventListener("input", me.onInput.bind(me));
    root.addEventListener("focusout", me.onFocusOut.bind(me));
  }
  connectedCallback() {
    var _a;
    const me = this;
    me.silentChangeValue = true;
    me.owner = (_a = me.elementData) == null ? void 0 : _a.owner;
    me.createStructure(me.owner.format);
    me.updateValue(me._value);
    me.silentChangeValue = false;
  }
  ingestDate(d) {
    let r = new Date(d);
    if (isNaN(r)) {
      r = DH.parse(d, this.owner.format);
    }
    return r;
  }
  get is24Hour() {
    return this.owner.is24Hour;
  }
  attributeChangedCallback(name, oldValue, value) {
    var _a;
    const me = this;
    switch (name) {
      case "min":
        me.updateMin(value);
        break;
      case "max":
        me.updateMax(value);
        break;
      case "label":
        me._label = value;
        (_a = me.inputs) == null ? void 0 : _a.forEach((e) => e.setAttribute("aria-label", value));
        break;
      case "disabled":
        me.disabled = value;
        break;
      case "tabindex":
        me.tabIndex = Number(value);
        break;
    }
  }
  updateMin(min) {
    const d = min == null ? null : DH.getTime(this.ingestDate(min));
    this.minValue = d;
    this.minHour = (d == null ? void 0 : d.getHours()) || 0;
  }
  updateMax(max) {
    const d = max == null ? null : DH.getTime(this.ingestDate(max));
    this.maxValue = d;
    this.maxHour = (d == null ? void 0 : d.getHours()) || (this.is24Hour ? 23 : 11);
  }
  /**
   * TimeFields always focus the first (Hours) subfield when focused
   */
  onFocusIn(event) {
    var _a;
    const { target } = event;
    if (target.tagName === "INPUT") {
      target.value = `00${target.value}`.slice(-2);
      (_a = target.setSelectionRange) == null ? void 0 : _a.call(target, 0, 2);
    }
    if (!this._root.contains(event.relatedTarget)) {
      if (!GlobalEvents.isMouseDown()) {
        this.focusField(0);
      }
    }
    event.stopImmediatePropagation();
  }
  onClick(event) {
    const { target } = event, { owner } = this;
    if (target === this.ampmButton) {
      if (!owner.readOnly) {
        target.innerText = target.innerText === DH.amIndicator ? DH.pmIndicator : DH.amIndicator;
        this.onValueMutated();
        event.stopPropagation();
      }
    }
  }
  onFocusOut({ target }) {
    this.onValueMutated();
    if (!this.isCleared && target !== this.ampmButton) {
      target.value = `0${parseInt(target.value)}`.slice(-2);
    }
    ;
  }
  onKeyDown(keyEvent) {
    var _a, _b, _c, _d;
    const me = this, {
      subfieldIndex
    } = me, {
      editable,
      readOnly
    } = me.owner, {
      amIndicator,
      pmIndicator
    } = DH, { target, key } = keyEvent;
    if (keyEvent.ctrlKey || keyEvent.cmdKey || keyEvent.metaKey || keyEvent.shiftKey && key !== "Tab" && key.length !== 1) {
      return;
    }
    switch (key) {
      case "Tab":
        me.inputs[keyEvent.shiftKey ? 0 : me.inputs.length - 1].focus();
        break;
      case " ":
        if (target === me.ampmButton || readOnly) {
          keyEvent.stopImmediatePropagation();
        }
        break;
      case "ArrowLeft":
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        if (subfieldIndex > 0 && !keyEvent.altKe) {
          me.focusField(subfieldIndex - 1);
        }
        break;
      case ":":
        if (target === me.ampmButton) {
          keyEvent.preventDefault();
          break;
        }
      case "ArrowRight":
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        if (subfieldIndex < me.inputs.length - 1) {
          me.focusField(subfieldIndex + 1);
        }
        break;
      case "ArrowUp":
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        if (!readOnly) {
          me[me.owner.pickerVisible ? "decrementSubfield" : "incrementSubfield"](target);
        }
        break;
      case "ArrowDown":
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        if (!readOnly) {
          me[me.owner.pickerVisible ? "incrementSubfield" : "decrementSubfield"](target);
        }
        break;
      case "0":
      case "1":
      case "2":
      case "3":
      case "4":
      case "5":
      case "6":
      case "7":
      case "8":
      case "9":
        if (!editable) {
          keyEvent.preventDefault();
        }
        break;
      case "Backspace":
      case "Delete":
        if (!editable) {
          keyEvent.preventDefault();
        }
        break;
      case "Escape":
        break;
      case ((_a = amIndicator == null ? void 0 : amIndicator[0]) == null ? void 0 : _a.toLocaleLowerCase()):
      case ((_b = amIndicator == null ? void 0 : amIndicator[0]) == null ? void 0 : _b.toLocaleUpperCase()):
        if (!readOnly && !me.is24Hour) {
          keyEvent.preventDefault();
          me.ampmButton.innerText = amIndicator;
          me.onValueMutated();
          break;
        }
      case ((_c = pmIndicator == null ? void 0 : pmIndicator[0]) == null ? void 0 : _c.toLocaleLowerCase()):
      case ((_d = pmIndicator == null ? void 0 : pmIndicator[0]) == null ? void 0 : _d.toLocaleUpperCase()):
        keyEvent.preventDefault();
        if (!readOnly && !me.is24Hour) {
          keyEvent.preventDefault();
          me.ampmButton.innerText = pmIndicator;
          me.onValueMutated();
          break;
        }
      default:
        keyEvent.preventDefault();
    }
  }
  onInput({ target }) {
    const me = this, { value } = target;
    if (value.length === 1 || value.length === 2 && target.selectionStart === 1 && target.selectionEnd === 1) {
      target.setSelectionRange(1, 2);
      return;
    }
    if (!value) {
      target.value = "00";
      target.setSelectionRange(0, 2);
    } else if (target.selectionEnd === 2) {
      const { subfieldIndex } = this;
      if (subfieldIndex < me.inputs.length - 1) {
        me.focusField(subfieldIndex + 1);
      }
    }
    if (target === me.hour && value === "00" && !me.is24Hour) {
      me.ampmButton.innerText = DH.amIndicator;
    }
    me.activeElement = target;
    me.onValueMutated();
    me.activeElement = null;
  }
  /**
   * Decrement the focused subfield (Hours, minutes or seconds).
   *
   * If the configured {@link #config-step}'s unit matches the subfield's unit,
   * the step magnitude is used, otherwise -1 is used.
   * @internal
   */
  decrementSubfield(field) {
    var _a;
    const {
      subfieldIndex,
      step,
      isPM
    } = this;
    if (field === this.ampmButton) {
      field.innerText = isPM ? DH.amIndicator : DH.pmIndicator;
    } else {
      const oldValue = this.getSubFieldValue(subfieldIndex), increment = step < magnitudes[subfieldIndex - 1] && step > magnitudes[subfieldIndex + 1] ? step / magnitudes[subfieldIndex] : 1, newValue = Math.floor(oldValue / increment) * increment - increment;
      if (subfieldIndex === 0 && !this.is24Hour && oldValue === 12) {
        this.ampmButton.innerText = DH.amIndicator;
      }
      field.value = newValue;
    }
    this.onValueMutated();
    (_a = field.setSelectionRange) == null ? void 0 : _a.call(field, 0, 2);
  }
  /**
   * Increment the focused subfield (Hours, minutes or seconds).
   *
   * If the configured {@link #config-step}'s unit matches the subfield's unit,
   * the step magnitude is used, otherwise -1 is used.
   * @internal
   */
  incrementSubfield(field) {
    var _a;
    const {
      subfieldIndex,
      step,
      is24Hour,
      isPM
    } = this;
    if (field === this.ampmButton) {
      field.innerText = isPM ? DH.amIndicator : DH.pmIndicator;
    } else {
      const field2 = this.inputs[subfieldIndex], oldValue = this.getSubFieldValue(subfieldIndex), increment = step < magnitudes[subfieldIndex - 1] && step > magnitudes[subfieldIndex + 1] ? step / magnitudes[subfieldIndex] : 1, newValue = Math.floor(oldValue / increment) * increment + increment;
      if (subfieldIndex === 0 && !this.is24Hour && oldValue === 11) {
        this.ampmButton.innerText = DH.pmIndicator;
      }
      if (subfieldIndex === 0) {
        const hourOfDay = isPM && !is24Hour ? oldValue + 12 : oldValue;
        if (hourOfDay === (this.maxHour || 23)) {
          return;
        }
      }
      field2.value = newValue;
    }
    this.onValueMutated();
    (_a = field.setSelectionRange) == null ? void 0 : _a.call(field, 0, 2);
  }
  /**
   * Creates the element structure:
   *
   * ```
   * <span></span><input><span></span><input><span></span><input><span></span>
   * ``
   *
   * Each time token, hours, minutes, seconds has an input field and there may be
   * boilerplate text from the format string at any positoin before, between or after them.
   * @param {String} format The {@link Core.helper.DateHelper} format string for the time field.
   * @returns {String} the shadow root's HTML sting.
   */
  createStructure(format = this.owner.format) {
    var _a;
    const me = this, {
      _root,
      tabIndex,
      is24Hour
    } = me, {
      amIndicator
    } = DH, testDate = new Date(1970, 0, 1, 1, 2, 3), timeString = DH.format(testDate, format).trim(), delimiter = me.delimiter = (_a = timeString.match(delimiterRe)) == null ? void 0 : _a[1], parts = [...timeString.matchAll(new RegExp(`\\d+${delimiter ? `|\\${delimiter}` : ""}|${amIndicator}|[^\\${delimiter}\\d]+`, "g"))].map((m) => m[0]), children = [];
    if (tabIndex != null) {
      me.setAttribute("tabIndex", tabIndex);
    }
    let subfieldIndex = 0, hasAmPmIndicator;
    for (let i = 0, inputCount = 0, { length } = parts; i < length; i++) {
      const part = parts[i], max = i ? 59 : is24Hour ? 23 : 11;
      if (part.trim() === amIndicator) {
        hasAmPmIndicator = true;
        children.push({
          tag: "button",
          reference: "ampmButton",
          id: "am-pm",
          type: "button",
          text: amIndicator,
          dataset: {
            subfieldIndex: subfieldIndex++
          }
        });
      } else if (part.match(numbers)) {
        children.push({
          tag: "input",
          id: names[inputCount],
          reference: names[inputCount++],
          size: 2,
          maxlength: 2,
          minlength: 2,
          dataset: {
            subfieldIndex: subfieldIndex++,
            max
          }
        });
      } else if (part === delimiter) {
        children.push({
          tag: "span",
          text: delimiter,
          reference: `delimiter`
        });
      } else {
        children.push({
          reference: "junk",
          tag: "span",
          text: part.replaceAll(" ", "\xA0")
        });
      }
    }
    if (children[children.length - 1].reference === "delimiter") {
      children.length--;
    }
    if (!is24Hour && !hasAmPmIndicator) {
      children.push({
        tag: "button",
        reference: "ampmButton",
        id: "am-pm",
        type: "button",
        text: amIndicator,
        dataset: {
          subfieldIndex: subfieldIndex++
        }
      });
    }
    children.push({
      tag: "style",
      text: styles
    });
    DomSync.sync({
      targetElement: _root,
      refOwner: me,
      domConfig: {
        onlyChildren: true,
        children
      }
    });
    me.inputs = [..._root.querySelectorAll("input")];
    if (!me.minute) {
      me.owner.step = "1 h";
    }
    if (!is24Hour) {
      me.inputs.push(me.ampmButton);
    } else {
      me.ampmButton = null;
    }
  }
  attachRef(name, el) {
    this.byRef[name] = this[name] = el;
  }
  detachRef(name) {
    this[name] = null;
    delete this.byRef[name];
  }
  getSubFieldValue(index) {
    const { date } = this;
    if (date) {
      return date[getMethod[index]]();
    } else {
      return 0;
    }
  }
  setSubFieldValue(field, value, silent) {
    field = typeof field === "number" ? this.inputs[field] : typeof field === "string" ? this[field] : field;
    if (field) {
      if (value < 10 && (field !== this.activeElement || field.value.length === 2)) {
        value = `0${value}`;
      }
      if (field.value != value) {
        field.value = value;
        if (!silent) {
          this.onValueMutated();
        }
      }
    }
  }
  set value(value) {
    const { _value: oldValue } = this;
    value = this._value = value ? this.ingestDate(value) : null;
    if (this.isConnected && value - oldValue) {
      this.updateValue(value, oldValue);
    }
  }
  updateValue(value, oldValue = null) {
    if (Number(value) != Number(oldValue)) {
      const me = this, { ampmButton } = me;
      if (value) {
        const { hour, minute, second } = me, h = value.getHours();
        if (me.is24Hour) {
          me.setSubFieldValue(hour, h, true);
        } else {
          ampmButton.innerText = h > 11 ? DH.pmIndicator : DH.amIndicator;
          me.setSubFieldValue(hour, h > 12 ? h % 12 : h, true);
        }
        minute && me.setSubFieldValue(minute, value.getMinutes(), true);
        second && me.setSubFieldValue(second, value.getSeconds(), true);
      } else {
        me._value = DH.clearTime(oldValue);
        me.inputs.forEach((i) => i.value = "");
        ampmButton && (ampmButton.innerText = DH.amIndicator);
      }
      if (!me.silentChangeValue && !me.owner.sycingInputFieldValue) {
        me.dispatchEvent(new InputEvent("change"));
      }
    }
  }
  onValueMutated() {
    const {
      date,
      minValue,
      maxValue
    } = this, newValue = date ? new Date(date) : null;
    if (date) {
      if (minValue && DH.getTime(newValue) < DH.getTime(minValue)) {
        newValue.setHours(minValue.getHours(), minValue.getMinutes(), minValue.getSeconds());
        this._value = null;
      } else if (maxValue && DH.getTime(newValue) > DH.getTime(maxValue)) {
        newValue.setHours(maxValue.getHours(), maxValue.getMinutes(), maxValue.getSeconds());
        this._value = null;
      }
    }
    this.value = newValue;
  }
  set disabled(disabled) {
    disabled = Boolean(disabled);
    if (this._disabled !== disabled) {
      this._disabled = disabled;
      if (disabled) {
        this.setAttribute("disabled", true);
      } else {
        this.removeAttribute("disabled");
      }
    }
  }
  set tabIndex(tabIndex) {
    this._tabIndex = tabIndex;
  }
  get tabIndex() {
    return this._tabIndex;
  }
  get subfieldIndex() {
    var _a;
    return parseInt((_a = this.shadowRoot.activeElement) == null ? void 0 : _a.dataset.subfieldIndex);
  }
  get isPM() {
    var _a;
    return this.is24Hour ? parseInt(this.hour.value || 0) > 11 : ((_a = this.ampmButton.firstChild) == null ? void 0 : _a.data) === DH.pmIndicator;
  }
  get value() {
    return this.date;
  }
  get innerText() {
    return DH.format(this.date, this.owner.format) || "";
  }
  get date() {
    var _a, _b, _c, _d;
    const me = this, { _value } = me;
    if (!me.isConnected) {
      return _value || null;
    }
    if (me.isCleared) {
      return null;
    }
    const result = new Date(me._value), h = parseInt(((_a = me.hour) == null ? void 0 : _a.value) || 0), minutes = parseInt(((_b = me.minute) == null ? void 0 : _b.value) || 0), seconds = parseInt(((_c = me.second) == null ? void 0 : _c.value) || 0);
    let hours = h;
    if (!me.is24Hour) {
      const isPM = ((_d = me.ampmButton.firstChild) == null ? void 0 : _d.data) === DH.pmIndicator;
      if (h === 12 && !me.isPM) {
        hours = 0;
      } else if (h < 12 && isPM) {
        hours += 12;
      }
    }
    result.setHours(hours, minutes, seconds);
    return result;
  }
  get rawDate() {
    var _a, _b, _c;
    const result = new Date(this._value);
    result.setHours(parseInt(((_a = this.hour) == null ? void 0 : _a.value) || 0), parseInt(((_b = this.minute) == null ? void 0 : _b.value) || 0), parseInt(((_c = this.second) == null ? void 0 : _c.value) || 0));
    return result;
  }
  get isCleared() {
    var _a;
    return Boolean(!((_a = this.inputs) == null ? void 0 : _a.map((i) => i.value.trim()).join("")));
  }
  focusField(index) {
    var _a;
    const input = this.inputs[index];
    if (input) {
      input.focus();
      (_a = input.setSelectionRange) == null ? void 0 : _a.call(input, 0, 2);
    }
  }
}
__publicField(TimeInput, "formAssociated", true);
__publicField(TimeInput, "observedAttributes", [
  "min",
  "max",
  "disabled",
  "tabindex"
]);
if (typeof customElements !== "undefined" && !customElements.get("bry-time")) {
  customElements.define("bry-time", TimeInput);
}
class TimeField extends PickerField {
  //endregion
  get inputElement() {
    const domConfig = super.inputElement;
    domConfig.tag = "bry-time";
    domConfig.label = this.label;
    domConfig.elementData = { owner: this };
    return domConfig;
  }
  //region Init & destroy
  changePicker(picker, oldPicker) {
    const me = this, pickerWidth = me.pickerWidth || (picker == null ? void 0 : picker.width);
    return TimePicker.reconfigure(oldPicker, picker, {
      owner: me,
      defaults: {
        step: me.step,
        value: me.value,
        forElement: me.pickerAlignElement,
        owner: me,
        is24Hour: me.is24Hour,
        align: {
          matchSize: pickerWidth == null,
          anchor: me.overlayAnchor,
          target: me[me.pickerAlignElement]
        },
        width: pickerWidth,
        onTimeChange({ source, time }) {
          me._isUserAction = true;
          source._isUserAction && source.hide();
          me.value = time;
          if (me.min && !me.is24Hour) {
            me._picker.widgetMap.hour.store.filter();
          }
          me._isUserAction = false;
        }
      }
    });
  }
  //endregion
  selectAll() {
  }
  syncInputFieldValue() {
    const me = this;
    me.sycingInputFieldValue = true;
    super.syncInputFieldValue(...arguments);
    me.sycingInputFieldValue = false;
    me.syncInvalid();
    if (me.pickerVisible) {
      me.picker.value = me.value;
    }
  }
  get inputValue() {
    return this._value;
  }
  get needsInputSync() {
    var _a;
    return String(this.input[this.inputValueAttr]) !== String((_a = this.inputValue) != null ? _a : "");
  }
  // Override at this level. We set the attribute.
  // But the custom element itself decides on its editability by interrogating its owner.
  syncInputReadOnly() {
    this.getConfig("readOnly");
    this.inputReadOnly = this._readOnly;
  }
  onBackClick() {
    const me = this, { min } = me;
    if (!me.readOnly && me.value) {
      const newValue = DH.add(me.value, -1 * me.step.magnitude, me.step.unit);
      if (!min || min.getTime() <= newValue) {
        me.value = newValue;
      }
    }
  }
  onForwardClick() {
    const me = this, { max } = me;
    if (!me.readOnly && me.value) {
      const newValue = DH.add(me.value, me.step.magnitude, me.step.unit);
      if (!max || max.getTime() >= newValue) {
        me.value = newValue;
      }
    }
  }
  //endregion
  // region Validation
  get isValid() {
    const me = this;
    me.clearError("L{minimumValueViolation}", true);
    me.clearError("L{maximumValueViolation}", true);
    let value = me.input.date;
    if (value) {
      value = DH.getTime(value);
      if (me._min && DH.getTime(me._min) > value) {
        me.setError("L{minimumValueViolation}", true);
        return false;
      }
      if (me._max && DH.getTime(me._max) < value) {
        me.setError("L{maximumValueViolation}", true);
        return false;
      }
    }
    return super.isValid;
  }
  internalOnChange(event) {
    const me = this;
    me.inputting = true;
    me._value = me.input.date;
    if (me.pickerVisible) {
      me.picker.value = me.input.date;
    }
    super.internalOnChange(...arguments);
    me.inputting = false;
  }
  internalOnKeyEvent(event) {
    if (this.pickerVisible) {
      if (event.key === "Escape") {
        this.value = this.pickerValueOnShow;
      }
      if (event.key === "Enter") {
        this.hidePicker();
        event.preventDefault();
        return;
      }
    }
    super.internalOnKeyEvent(event);
  }
  hasChanged(oldValue, newValue) {
    if ((oldValue == null ? void 0 : oldValue.getTime) && (newValue == null ? void 0 : newValue.getTime)) {
      return this.keepDate ? oldValue - newValue !== 0 : !DH.isSameTime(oldValue, newValue);
    }
    return super.hasChanged(oldValue, newValue);
  }
  //endregion
  //region Toggle picker
  /**
   * Show picker
   */
  showPicker() {
    const me = this, {
      picker,
      value
    } = me;
    if (me.readOnly) {
      return;
    }
    picker.value = null;
    picker.format = me.format;
    picker.max = me.max;
    picker.min = me.min;
    super.showPicker();
    picker.value = me.pickerValueOnShow = value;
    if (!value) {
      me.value = picker.value;
    }
  }
  //endregion
  //region Getters/setters
  transformTimeValue(value) {
    if (value != null) {
      if (typeof value === "string") {
        value = DH.parse(value, this.format);
        if (this.keepDate && value && this.value) {
          value = DH.copyTimeValues(new Date(this.value), value);
        }
      } else {
        value = new Date(value);
      }
      if (DH.isValidDate(value)) {
        if (!this.keepDate) {
          value = DH.getTime(value);
        }
      } else {
        value = null;
      }
    }
    const eventObject = { value };
    this.trigger("transformTimeValue", eventObject);
    value = eventObject.value;
    return value;
  }
  changeMin(value) {
    return this.transformTimeValue(value);
  }
  updateMin(value) {
    const { input } = this;
    if (input) {
      if (value == null) {
        input.removeAttribute("min");
      } else {
        input.setAttribute("min", value);
      }
    }
    this.syncInvalid();
  }
  changeMax(value) {
    return this.transformTimeValue(value);
  }
  updateMax(value) {
    const { input } = this;
    if (input) {
      if (value == null) {
        input.removeAttribute("max");
      } else {
        input.setAttribute("max", value);
      }
    }
    this.syncInvalid();
  }
  changeValue(value, was) {
    const me = this, newValue = me.transformTimeValue(value);
    if (value && !newValue || me.isRequired && value === "") {
      me.setError("L{invalidTime}");
      return;
    }
    me.clearError("L{invalidTime}");
    if (me.hasChanged(was, newValue)) {
      return super.changeValue(newValue, was);
    }
    if (!me.inputting) {
      me.syncInputFieldValue(true);
    }
  }
  updateValue(value, was) {
    const { expand } = this.triggers;
    this.syncInputFieldValue(true);
    if (expand && value) {
      expand.element.firstElementChild.style.animationDelay = -((value.getHours() * 60 + value.getMinutes()) / 10) + "s";
    }
    super.updateValue(value, was);
  }
  changeStep(value, was) {
    const type = typeof value;
    if (!value) {
      return { magnitude: 5, unit: "minute" };
    }
    if (type === "number") {
      value = {
        magnitude: Math.abs(value),
        unit: was ? was.unit : "hour"
      };
    } else if (type === "string") {
      value = DH.parseDuration(value);
    }
    if ((value == null ? void 0 : value.unit) && (value == null ? void 0 : value.magnitude)) {
      if (value.magnitude < 0) {
        value = {
          magnitude: -value.magnitude,
          // Math.abs
          unit: value.unit
        };
      }
      return value;
    }
  }
  updateStep(value) {
    const { _picker } = this;
    this.input.step = value && DH.as("s", value.magnitude, value.unit);
    _picker && DH.as("m", value) < 30 && (_picker.step = value);
    this.syncInvalid();
  }
  get is24Hour() {
    return DH.is24HourFormat(this.format);
  }
  updateFormat(format, oldFormat) {
    const me = this;
    if (!me.isConfiguring) {
      me._format = oldFormat;
      const { input } = me, { value } = input;
      me._format = format;
      me.sycingInputFieldValue = true;
      input.createStructure(format);
      input.updateValue(value, NaN);
      me.syncInputFieldValue(true);
      me.sycingInputFieldValue = false;
    }
  }
  //endregion
  //region Localization
  updateLocalization() {
    super.updateLocalization();
    if (this.input.isConnected) {
      this.input.createStructure();
    }
    this.syncInputFieldValue(true);
  }
  //endregion
}
//region Config
__publicField(TimeField, "$name", "TimeField");
__publicField(TimeField, "type", "timefield");
__publicField(TimeField, "alias", "time");
__publicField(TimeField, "configurable", {
  inputElementTag: "bry-time",
  picker: {
    type: "timepicker",
    scrollAction: "realign",
    align: {
      align: "t0-b0",
      minHeight: 200,
      axisLock: true,
      matchSize: "min"
    }
  },
  /**
   * Get/Set format for time displayed in field (see {@link Core.helper.DateHelper#function-format-static}
   * for formatting options).
   * @member {String} format
   */
  /**
   * Format for date displayed in field (see Core.helper.DateHelper#function-format-static for formatting
   * options).
   * @config {String}
   * @default
   */
  format: "LT",
  /**
   * Widgets that trigger functionality upon click. Each trigger icon is a {@link Core.widget.Widget} instance
   * which may be hidden, shown and observed and styled just like any other widget.
   * @prp {Object<String,Core.widget.Widget>} triggers
   * @accepts {Object<String,FieldTriggerConfig>}
   * @property {FieldTriggerConfig} triggers.expand Expands the picker to select a time
   * @property {FieldTriggerConfig} triggers.back Subtracts the {@link #config-step} from the current time
   * @property {FieldTriggerConfig} triggers.forward Adds the {@link #config-step} to the current time
   * @property {FieldTriggerConfig} triggers.clear Clears the field value, only available if this field is
   * {@link #config-clearable}
   */
  triggers: {
    expand: {
      align: "end",
      handler: "onTriggerClick",
      key: " ",
      compose: () => ({
        children: [{
          class: {
            "b-icon-clock-live": 1
          }
        }]
      })
    },
    back: {
      align: "start",
      cls: "b-icon b-icon-angle-left b-step-trigger",
      key: "Shift+ArrowDown",
      handler: "onBackClick"
    },
    forward: {
      align: "end",
      cls: "b-icon b-icon-angle-right b-step-trigger",
      key: "Shift+ArrowUp",
      handler: "onForwardClick"
    }
  },
  /**
   * Get/set min value, which can be a Date or a string. If a string is specified, it will be converted using
   * the specified {@link #config-format}.
   * @member {Date} min
   * @accepts {String|Date}
   */
  /**
   * Min time value
   * @config {String|Date}
   */
  min: null,
  /**
   * Get/set max value, which can be a Date or a string. If a string is specified, it will be converted using
   * the specified {@link #config-format}.
   * @member {Date} max
   * @accepts {String|Date}
   */
  /**
   * Max time value
   * @config {String|Date}
   */
  max: null,
  /**
   * The `step` property may be set in Object form specifying two properties, `magnitude`, a Number, and
   * `unit`, a String.
   *
   * If a Number is passed, the steps's current unit is used and just the magnitude is changed.
   *
   * If a String is passed, it is parsed by {@link Core.helper.DateHelper#function-parseDuration-static}, for
   * example `'5m'`, `'5 m'`, `'5 min'`, `'5 minutes'`.
   *
   * Upon read, the value is always returned in object form containing `magnitude` and `unit`.
   * @member {DurationConfig} step
   * @accepts {String|Number|DurationConfig}
   */
  /**
   * Time increment duration value. Defaults to 5 minutes.
   * The value is taken to be a string consisting of the numeric magnitude and the units.
   * The units may be a recognised unit abbreviation of this locale or the full local unit name.
   * For example `"10m"` or `"5min"` or `"2 hours"`
   * @config {String|Number|DurationConfig}
   */
  step: "5m",
  /**
   * Set to `false` to hide the forward and backward time step triggers.
   * @config {Boolean}
   * @default true
   */
  stepTriggers: {
    $config: null,
    default: true
  },
  /**
   * Get/set value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}.
   * @member {Date} value
   * @accepts {String|Date}
   */
  /**
   * Value, which can be a Date or a string. If a string is specified, it will be converted using the
   * specified {@link #config-format}
   * @config {String|Date}
   */
  value: {
    $config: {
      equal: "date"
    },
    value: null
  },
  /**
   * Set to true to not clean up the date part of the passed value. Set to false to reset the date part to
   * January 1st
   * @prp {Boolean}
   * @default
   */
  keepDate: true
});
__publicField(TimeField, "delayable", {
  // Buffer the syncing of validity so that momentary invalid states
  // encountered during typing are suppressed if typing is at reasonable speed.
  syncInvalid: {
    type: "buffer",
    delay: 300
  }
});
TimeField.initClass();
TimeField._$name = "TimeField";
export {
  TimeField as default
};
